import type { MessageDetail } from '@dte/otw-azure-functions/src/common/interfaces/api/messageDetail/MessageDetail';
import { MessageIcon } from '@dte/otw-azure-functions/src/common/interfaces/api/messageDetail/MessageIcon';
import { Grid } from '@mantine/core';
import Image from 'next/image';
import type { PropsWithChildren, ReactNode } from 'react';
import Markdown from 'react-markdown';
import causeIcon from '../../images/icons/status/causeIcon.svg';
import checkMarkIcon from '../../images/icons/status/checkMarkIcon.svg';
import clockIcon from '../../images/icons/status/clockIcon.svg';
import exclamationIcon from '../../images/icons/status/exclamationIcon.svg';
import heartHand from '../../images/icons/status/heartHand.svg';
import mapPinIcon from '../../images/icons/status/mapPinIcon.svg';
import powerOffIcon from '../../images/icons/status/powerOffIcon.svg';
import powerOnIcon from '../../images/icons/status/powerOnIcon.svg';
import powerUnknownIcon from '../../images/icons/status/powerUnknownIcon.svg';
import { BlankIcon } from '../icons/blank';
import styles from './MessageDetailDisplay.module.scss';

const svgIcons: Record<string, ReactNode> = {};

svgIcons[MessageIcon.MapPin] = <Image src={mapPinIcon} width={25} height={25} alt="Map pin" />;
svgIcons[MessageIcon.PowerOff] = <Image src={powerOffIcon} width={25} height={25} alt="Power off" />;
svgIcons[MessageIcon.PowerOn] = <Image src={powerOnIcon} width={25} height={25} alt="Power on" />;
svgIcons[MessageIcon.PowerUnknown] = <Image src={powerUnknownIcon} width={25} height={25} alt="Power unknown" />;
svgIcons[MessageIcon.Exclamation] = <Image src={exclamationIcon} width={25} height={25} alt="Exclamation mark" />;
svgIcons[MessageIcon.Clock] = <Image src={clockIcon} width={25} height={25} alt="Clock" />;
svgIcons[MessageIcon.Checkmark] = <Image src={checkMarkIcon} width={25} height={25} alt="Check mark" />;
svgIcons[MessageIcon.MagnifierElectric] = <Image src={causeIcon} width={25} height={25} alt="Magnifying glass" />;
svgIcons[MessageIcon.HeartHand] = (
	<Image src={heartHand} width={25} height={25} alt="Shaking hands forming the shape of a heart" />
);

export function MessageDetailIcon({ iconName }: { iconName: string | undefined }): ReactNode {
	if (!iconName) {
		return <BlankIcon />;
	}
	const icon = svgIcons[iconName];
	if (!icon) {
		return <BlankIcon />;
	}
	return icon;
}

export interface MessageDetailDisplayProps extends PropsWithChildren {
	messageDetail: MessageDetail | undefined;
}

export function MessageDetailLabel({ children }: PropsWithChildren): ReactNode {
	if (!children) {
		return null;
	}

	return <h4>{children}</h4>;
}

export function MessageDetailMessage({ messageDetail }: MessageDetailDisplayProps): ReactNode {
	const message = messageDetail?.message;
	if (!message) {
		return null;
	}

	return (
		<p>
			<b>{message}</b>
		</p>
	);
}

export function MessageDetailDetails({ messageDetail }: MessageDetailDisplayProps): ReactNode {
	const details = messageDetail?.details;
	if (!details) {
		return null;
	}

	return <Markdown>{details}</Markdown>;
}

export function MessageDetailDisplay({ messageDetail, children }: MessageDetailDisplayProps): ReactNode {
	if (!messageDetail) {
		return null;
	}

	return (
		<Grid className={styles['main']}>
			<Grid.Col span={'content'}>
				<MessageDetailIcon iconName={messageDetail.icon} />
			</Grid.Col>
			<Grid.Col span={'auto'}>
				<MessageDetailLabel>{children}</MessageDetailLabel>
				<MessageDetailMessage messageDetail={messageDetail} />
				<MessageDetailDetails messageDetail={messageDetail} />
			</Grid.Col>
		</Grid>
	);
}
